import React, { useState, useEffect } from 'react';
import { Card, CardTitle, Insight, Btn } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

const SETUP_STEPS = [
  { n: '1', title: 'Create a bot', desc: 'Open Telegram → search @BotFather → send /newbot → follow prompts → copy the bot token.' },
  { n: '2', title: 'Add token to Vercel', desc: 'Go to vercel.com/sanoire/sanoire-admin/settings/environment-variables → add TELEGRAM_BOT_TOKEN with your token.' },
  { n: '3', title: 'Create a channel', desc: 'In Telegram, create a public channel (e.g. @SanoireUpdates). Add your bot as an admin with "Post messages" permission.' },
  { n: '4', title: 'Get the channel ID', desc: 'Forward a message from your channel to @userinfobot → it will show the channel ID (e.g. -1001234567890).' },
  { n: '5', title: 'Send a test', desc: 'Use the composer below to send a test message to your channel ID.' },
];

function timeAgo(ts) {
  if (!ts) return '';
  const ms = Date.now() - new Date(ts);
  if (ms < 3600000) return Math.round(ms / 60000) + 'm ago';
  if (ms < 86400000) return Math.round(ms / 3600000) + 'h ago';
  return Math.round(ms / 86400000) + 'd ago';
}

export default function Telegram() {
  const [chatId,   setChatId]   = useState('');
  const [message,  setMessage]  = useState('');
  const [sending,  setSending]  = useState(false);
  const [result,   setResult]   = useState(null);
  const [history,  setHistory]  = useState([]);
  const [configured, setConfigured] = useState(null);

  useEffect(() => {
    // Check if bot is configured by pinging send-telegram
    (async () => {
      try {
        const r = await fetch(window.location.origin + '/api/send-telegram', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: 'ping', chat_ids: [] }),
        });
        const d = await r.json();
        setConfigured(!d.setup); // 'setup' key means not configured
      } catch (_e) { setConfigured(false); }
    })();

    (async () => {
      try {
        const { data } = await supabase
          .from('comms_broadcasts')
          .select('*')
          .contains('channels', ['telegram'])
          .order('sent_at', { ascending: false }).limit(20);
        setHistory(data || []);
      } catch (_e) {}
    })();
  }, []);

  async function handleSend() {
    if (!chatId.trim() || !message.trim() || sending) return;
    setSending(true); setResult(null);
    try {
      const r = await fetch(window.location.origin + '/api/send-telegram', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, chat_ids: [chatId.trim()] }),
      });
      const d = await r.json();
      setResult(d);
      if (d.sent > 0) {
        setMessage('');
        // Log to broadcasts
        await supabase.from('comms_broadcasts').insert({
          message, channels: ['telegram'],
          results: { telegram: d }, sent_at: new Date().toISOString(),
        });
        const { data } = await supabase
          .from('comms_broadcasts').select('*')
          .contains('channels', ['telegram'])
          .order('sent_at', { ascending: false }).limit(20);
        setHistory(data || []);
      }
    } catch (e) { setResult({ error: e.message }); }
    setSending(false);
  }

  return (
    <div style={{ fontFamily: F }}>

      {/* Setup status */}
      {configured === false && (
        <Card style={{ marginBottom: 16 }}>
          <CardTitle>Setup required</CardTitle>
          <Insight type="warn" style={{ marginBottom: 14 }}>
            TELEGRAM_BOT_TOKEN not found in Vercel environment variables. Follow the steps below.
          </Insight>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {SETUP_STEPS.map(step => (
              <div key={step.n} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#314F5C',
                  color: '#AFE1F6', fontSize: '.65rem', fontWeight: 700, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{step.n}</div>
                <div>
                  <div style={{ fontSize: '.74rem', fontWeight: 600, color: '#1a3040', marginBottom: 2 }}>{step.title}</div>
                  <div style={{ fontSize: '.68rem', color: '#9eb8c8', lineHeight: 1.5 }}>{step.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 14 }}>
            <a href="https://vercel.com/sanoire/sanoire-admin/settings/environment-variables"
              target="_blank" rel="noreferrer"
              style={{ fontSize: '.75rem', color: '#314F5C', textDecoration: 'underline', fontWeight: 500 }}>
              Open Vercel env settings →
            </a>
          </div>
        </Card>
      )}

      {configured === true && (
        <Insight type="ok" style={{ marginBottom: 16 }}>Bot connected — ready to send.</Insight>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 16 }}>

        {/* Composer */}
        <Card style={{ marginBottom: 0 }}>
          <CardTitle>Send message</CardTitle>

          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: '.62rem', fontWeight: 600, color: '#9eb8c8',
              textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>Channel ID or @username</div>
            <input value={chatId} onChange={e => setChatId(e.target.value)}
              placeholder="e.g. @SanoireUpdates or -1001234567890"
              style={{ width: '100%', padding: '7px 10px', borderRadius: 8, boxSizing: 'border-box',
                border: '1px solid #d8eaf4', fontSize: '.8rem', color: '#1a3040',
                fontFamily: F, outline: 'none' }}
            />
          </div>

          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: '.62rem', fontWeight: 600, color: '#9eb8c8',
              textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>Message
              <span style={{ fontWeight: 400, marginLeft: 6, textTransform: 'none', letterSpacing: 0 }}>HTML supported: &lt;b&gt;, &lt;i&gt;, &lt;a href&gt;</span>
            </div>
            <textarea value={message} onChange={e => setMessage(e.target.value)}
              rows={5} placeholder={'Hello from Sanoire!\n\nNew locations added in Bangkok \u2192 sanoire.com'}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, boxSizing: 'border-box',
                border: '1px solid #d8eaf4', fontSize: '.8rem', color: '#1a3040',
                fontFamily: F, outline: 'none', resize: 'vertical', lineHeight: 1.55 }}
            />
          </div>

          {/* Preview */}
          {message && (
            <div style={{ marginBottom: 10, background: '#f0f6ff', borderRadius: 10,
              padding: '10px 13px', border: '1px solid #d0e6f7' }}>
              <div style={{ fontSize: '.6rem', color: '#5591b8', fontWeight: 600, marginBottom: 3 }}>✉ Preview</div>
              <div style={{ fontSize: '.78rem', color: '#1a3040', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{message}</div>
            </div>
          )}

          {result && (
            <Insight type={result.error || result.failed > 0 ? 'warn' : 'ok'} style={{ marginBottom: 10 }}>
              {result.error ? result.error : result.sent + ' sent' + (result.failed > 0 ? ', ' + result.failed + ' failed' : '')}
            </Insight>
          )}

          <button onClick={handleSend} disabled={!chatId.trim() || !message.trim() || sending}
            style={{ width: '100%', padding: '9px', borderRadius: 8,
              background: chatId && message && !sending ? '#185FA5' : '#d8eaf4',
              color: chatId && message && !sending ? '#fff' : '#9eb8c8',
              border: 'none', fontFamily: F, fontSize: '.8rem', fontWeight: 600,
              cursor: chatId && message && !sending ? 'pointer' : 'default' }}>
            {sending ? 'Sending…' : 'Send to Telegram →'}
          </button>
        </Card>

        {/* History */}
        <Card style={{ marginBottom: 0 }}>
          <CardTitle>Message history</CardTitle>
          {history.length === 0 ? (
            <div style={{ fontSize: '.75rem', color: '#9eb8c8' }}>No Telegram messages sent yet.</div>
          ) : (
            history.map((b, i) => (
              <div key={i} style={{ padding: '8px 0', borderBottom: '.5px solid rgba(49,79,92,.06)' }}>
                <div style={{ fontSize: '.68rem', color: '#9eb8c8', marginBottom: 3 }}>{timeAgo(b.sent_at)}</div>
                <div style={{ fontSize: '.75rem', color: '#1a3040', lineHeight: 1.5,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.message}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                  {b.results?.telegram?.sent > 0 && (
                    <span style={{ fontSize: '.6rem', padding: '1px 6px', borderRadius: 6,
                      background: '#E1F5EE', color: '#085041', fontWeight: 500 }}>Sent</span>
                  )}
                  {b.results?.telegram?.error && (
                    <span style={{ fontSize: '.6rem', padding: '1px 6px', borderRadius: 6,
                      background: '#FCEBEB', color: '#501313', fontWeight: 500 }}>Failed</span>
                  )}
                </div>
              </div>
            ))
          )}
        </Card>
      </div>
    </div>
  );
}
